import { DateTime, Interval } from 'luxon';
import type { PayPeriodType } from './domain';

/** Local calendar date (YYYY-MM-DD) of a UTC instant in an IANA zone. */
export function localDate(utc: string | Date, tz: string): string {
  const dt = typeof utc === 'string' ? DateTime.fromISO(utc, { zone: 'utc' }) : DateTime.fromJSDate(utc);
  return dt.setZone(tz).toISODate()!;
}

/** Minutes after local midnight of a UTC instant in a zone. */
export function localMinuteOfDay(utc: string | Date, tz: string): number {
  const dt = (typeof utc === 'string' ? DateTime.fromISO(utc, { zone: 'utc' }) : DateTime.fromJSDate(utc)).setZone(tz);
  return dt.hour * 60 + dt.minute;
}

/** ISO weekday of a local date: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(date: string): number {
  return DateTime.fromISO(date, { zone: 'utc' }).weekday;
}

/** UTC ISO string for a local date + HH:MM in a zone (DST-correct). */
export function localToUtc(date: string, hhmm: string, tz: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return DateTime.fromISO(date, { zone: tz }).set({ hour: h, minute: m, second: 0, millisecond: 0 }).toUTC().toISO()!;
}

export function addDays(date: string, days: number): string {
  return DateTime.fromISO(date, { zone: 'utc' }).plus({ days }).toISODate()!;
}

export function daysBetween(from: string, to: string): number {
  return Math.round(DateTime.fromISO(to, { zone: 'utc' }).diff(DateTime.fromISO(from, { zone: 'utc' }), 'days').days);
}

export function dateRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

/** Local midnight at the start of a date, as UTC ISO. */
export function startOfLocalDayUtc(date: string, tz: string): string {
  return DateTime.fromISO(date, { zone: tz }).startOf('day').toUTC().toISO()!;
}

/** The pay period (inclusive local dates) containing `date`. */
export function periodContaining(
  date: string,
  anchorStart: string,
  type: PayPeriodType,
): { start: string; end: string } {
  const len = type === 'weekly' ? 7 : 14;
  const offset = daysBetween(anchorStart, date);
  const k = Math.floor(offset / len);
  const start = addDays(anchorStart, k * len);
  return { start, end: addDays(start, len - 1) };
}

/** Display helpers: Australian conventions (d MMM yyyy, h:mm am, zone shown). */
export function fmtDate(date: string): string {
  return DateTime.fromISO(date, { zone: 'utc' }).toFormat('ccc d LLL yyyy');
}
export function fmtTime(utc: string, tz: string, withZone = true): string {
  const dt = DateTime.fromISO(utc, { zone: 'utc' }).setZone(tz).setLocale('en-AU');
  return dt.toFormat(withZone ? 'h:mm a ZZZZ' : 'h:mm a').replace('AM', 'am').replace('PM', 'pm');
}
export function fmtDateTime(utc: string, tz: string): string {
  const dt = DateTime.fromISO(utc, { zone: 'utc' }).setZone(tz).setLocale('en-AU');
  return dt.toFormat('ccc d LLL, h:mm a ZZZZ').replace('AM', 'am').replace('PM', 'pm');
}
/** 7.5 -> "7:30" */
export function fmtHM(hours: number): string {
  const totalMin = Math.round(hours * 60);
  const sign = totalMin < 0 ? '-' : '';
  const abs = Math.abs(totalMin);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
}
export function minutesBetween(aUtc: string, bUtc: string): number {
  return Interval.fromDateTimes(DateTime.fromISO(aUtc), DateTime.fromISO(bUtc)).length('minutes');
}
